import mergeObjects from 'lodash/merge';
import type { PartialDeep } from 'type-fest';
import * as Yup from 'yup';

import { getTenantUnits } from '@core/configuration';
import { TenantDefaultMargins } from '@core/types/tenantDefaultMargins';
import { hasStringValue } from '@core/utils/equality';

import { marginValidators } from '../formik/margins';
import { Currency } from '../types/api/Currency/Currency';
import {
  DeliveryCost,
  NewDeliveryCostFromWireFormat,
} from '../types/api/DeliveryCost/DeliveryCost';
import Enums from '../types/api/Enums';
import { Forecast } from '../types/api/Forecast/Forecast';
import { NewPlantFromWireFormat, type Plant, PlantWireFormat } from '../types/api/Plant/Plant';
import {
  ReadyMixConstants,
  ReadyMixConstantsWireFormat,
} from '../types/api/ReadyMixConstants/ReadyMixConstants';
import type { PartialExcept } from '../types/app';
import {
  cleanedDecimal,
  cleanedNullableDecimal,
  formatDecimalString,
  percentToRatio,
  ratioToPercent,
} from '../utils/decimals';
import { resolveCurrencyCode } from '../utils/format';
import {
  FormikAddress,
  FormikCost,
  FormikCostRule,
  FormikDeliveryCosts,
  FormikNullableIntegrationSummary,
} from '../utils/formikHelpers';
import { logger } from '../utils/logger';
import { selectMarginOverrideDisplay } from '../utils/margins';
import { RemoveNullProperties } from '../utils/types';
import { NIL_UUID } from '../utils/uuid';
import {
  TestForUndefined,
  YupAddress,
  YupCost,
  YupDeliveryCosts,
  YupNullableCostRule,
  YupNullableCurrency,
  YupNullableDecimal,
  YupNullableString,
  YupPlantBudgetCapacityForecasts,
  YupReference,
  type YupSchemaDeliveryCostsType,
  type YupSchemaNullableDeliveryCostsType,
  type YupSchemaNullableReferenceType,
  type YupSchemaPlantBudgetCapacityForecastsType,
  type YupSchemaReferenceType,
  YupString,
  nonNegativeCost,
} from '../utils/yupHelpers';

export type PlantsBulkEditFormikType = {
  market: YupSchemaNullableReferenceType;
};

export type PlantsBulkEditRequestPayload = {
  id: string;
  market?: PartialExcept<Plant['market'], 'id'>;
};

export const PlantsBulkEditSchemaFormik: Yup.SchemaOf<PlantsBulkEditFormikType> =
  Yup.object().shape({
    market: YupReference('Market'),
  });

type DomainPlantFormikType = Omit<
  PlantWireFormat,
  'forecasts' | 'deliveryCosts' | 'market' | 'customFields' | 'divisionId'
>;

export type PlantFormikType = DomainPlantFormikType & {
  readyMixConstants: DomainPlantFormikType['readyMixConstants'] & {
    targetMargin?: string;
    minimumMargin?: string;
    marginsInheritedFrom: string;
  };
  budgetForecasts: YupSchemaPlantBudgetCapacityForecastsType;
  capacityForecasts: YupSchemaPlantBudgetCapacityForecastsType;
  deliveryCosts: YupSchemaDeliveryCostsType;
  market: YupSchemaReferenceType;
};

const createSharedPlantSchemaFields = (marginConfiguration?: Enums.MarginConfiguration): any => {
  const { minimumValidator, targetValidator } = marginValidators(
    marginConfiguration ?? Enums.MarginConfiguration.Percentage,
  );

  const readyMixConstantsSchema = Yup.object()
    .label('Ready mix constants')
    .shape({
      sgaCost: YupCost('SGA cost').test(nonNegativeCost),
      operatingCost: YupCost('Operating cost').test(nonNegativeCost),
      // percentages are backend decimals (0.05 == 5%)
      targetMarginPercentageOverride: YupNullableDecimal('Target margin % override'),
      minimumMarginPercentageOverride: YupNullableDecimal('Minimum margin % override'),
      // flat amounts are Currency
      targetMarginFlatAmountOverride: YupNullableCurrency('Target margin $ override'),
      minimumMarginFlatAmountOverride: YupNullableCurrency('Minimum margin $ override'),
      minimumMargin: minimumValidator,
      targetMargin: targetValidator,
    });

  return {
    name: YupString('Required'),
    externalID: YupNullableString('External ID'),
    address: YupAddress('Address'),
    market: YupReference('Market'),
    notes: YupNullableString('Notes (Internal Only)'),
    category: YupString('Location category'),
    currencyCode: YupString('Currency code'),
    deliveryCosts: YupDeliveryCosts('Delivery costs'),
    readyMixConstants: readyMixConstantsSchema,
    materialCostRule: YupNullableCostRule('Material cost rule'),
  };
};

export const createPlantSchemaFormik = (marginConfiguration?: Enums.MarginConfiguration): any => {
  const sharedFields = createSharedPlantSchemaFields(marginConfiguration);

  return Yup.object()
    .shape({
      ...sharedFields,
      budgetForecasts: YupPlantBudgetCapacityForecasts('Budget forecast'),
      capacityForecasts: YupPlantBudgetCapacityForecasts('Capacity forecast'),
    })
    .test(TestForUndefined('PlantSchemaFormik'));
};

export const createPlantSchemaWire = (marginConfiguration?: Enums.MarginConfiguration): any => {
  const sharedFields = createSharedPlantSchemaFields(marginConfiguration);

  return Yup.object()
    .shape({
      ...sharedFields,
      // Plant wire payload only ever contains budget/capacity forecasts (see
      // NewPlantFromFormik), which are not category-specific. Use the nullable
      // helper so productCategory may serialize as null to the server.
      forecasts: YupPlantBudgetCapacityForecasts('Forecasts'),
    })
    .test(TestForUndefined('PlantSchemaWire'));
};

// Default schemas for backward compatibility
export const PlantSchemaFormik = createPlantSchemaFormik();
export const PlantSchemaWire = createPlantSchemaWire();

const FormikReadyMixConstants = (
  rmcData: ReadyMixConstants | undefined,
  marginConfiguration: Enums.MarginConfiguration,
  defaultMargins: TenantDefaultMargins,
  currencyCode?: string | null,
): ReadyMixConstantsWireFormat & {
  targetMargin: string;
  minimumMargin: string;
  marginsInheritedFrom: string;
} => {
  // For placeholder UX, only populate form value when an override exists; otherwise leave empty
  const { targetMarginOverride: plantTargetMargin, minimumMarginOverride: plantMinimumMargin } =
    selectMarginOverrideDisplay(rmcData, marginConfiguration);

  let minimumMargin = plantMinimumMargin;
  let targetMargin = plantTargetMargin;
  let marginsInheritedFrom = 'location';
  if (!(hasStringValue(minimumMargin) && hasStringValue(targetMargin))) {
    if (marginConfiguration === Enums.MarginConfiguration.Percentage) {
      minimumMargin = ratioToPercent(defaultMargins.minimumMarginPercentage);
      targetMargin = ratioToPercent(defaultMargins.targetMarginPercentage);
    } else if (marginConfiguration === Enums.MarginConfiguration.FlatDollar) {
      minimumMargin = formatDecimalString(defaultMargins.minimumMarginFlatAmount, 2);
      targetMargin = formatDecimalString(defaultMargins.targetMarginFlatAmount, 2);
    }
    marginsInheritedFrom = 'tenant';
  }

  return {
    sgaCost: FormikCost(rmcData?.sgaCost, currencyCode),
    operatingCost: FormikCost(rmcData?.operatingCost, currencyCode),

    // form-only UI fields
    targetMargin,
    minimumMargin,
    marginsInheritedFrom,

    // pass-through original overrides so we can preserve them on save
    targetMarginPercentageOverride: rmcData?.targetMarginPercentageOverride ?? null,
    minimumMarginPercentageOverride: rmcData?.minimumMarginPercentageOverride ?? null,
    targetMarginFlatAmountOverride: rmcData?.targetMarginFlatAmountOverride ?? null,
    minimumMarginFlatAmountOverride: rmcData?.minimumMarginFlatAmountOverride ?? null,
  };
};

export const FormikPlant = (
  plantData: Partial<Plant> | undefined,
  marginConfiguration?: Enums.MarginConfiguration,
  defaultMargins?: TenantDefaultMargins,
): PlantFormikType => ({
  id: plantData?.id ?? NIL_UUID,
  name: plantData?.name ?? '',
  externalID: plantData?.externalID ?? null,
  integrationSummary: FormikNullableIntegrationSummary(plantData?.integrationSummary),
  address: FormikAddress(plantData?.address),
  market: {
    id: plantData?.market?.id ?? null,
    option:
      plantData?.market === undefined
        ? null
        : {
            value: plantData.market.id,
            label: plantData.market.name,
          },
  },
  notes: plantData?.notes ?? null,
  category: plantData?.category ?? Enums.PlantCategory.ReadyMix,
  currencyCode: plantData?.currencyCode ?? '',
  readyMixConstants: FormikReadyMixConstants(
    plantData?.readyMixConstants ?? undefined,
    marginConfiguration ?? Enums.MarginConfiguration.Percentage,
    defaultMargins ?? {
      targetMarginFlatAmount: '0',
      minimumMarginFlatAmount: '0',
      targetMarginPercentage: '0',
      minimumMarginPercentage: '0',
    },
    plantData?.currencyCode,
  ),
  deliveryCosts: FormikDeliveryCosts(
    plantData?.deliveryCosts ?? undefined,
    plantData?.currencyCode,
  ),
  budgetForecasts: Forecast.fillForecasts({
    currentForecasts: plantData?.forecasts ?? [],
    kind: Enums.ForecastKind.Budget,
    tenantUnits: getTenantUnits(),
    // Do not pass productCategory for plant-level budget/capacity forecasts:
    // existing DB rows have NULL product_category (returned as "" by the server),
    // so a Mix filter would silently hide them. Budget/capacity are not
    // category-specific, so no filter is needed.
  }).map((pf): PlantFormikType['budgetForecasts'][number] => ({
    id: pf.id,
    kind: pf.kind,
    value: pf.value,
    unit: pf.unit,
    // Plant budget/capacity forecasts are not category-specific.
    //  - Fresh fill rows (id === NIL_UUID) get null. The Forecast constructor
    //    defaults `undefined` to Mix; that default is irrelevant here because
    //    these forecasts have no category semantically.
    //  - Existing DB rows preserve whatever the server returned. Server NULL
    //    arrives as "" which we normalize to null; any real category value
    //    (legacy data) is preserved as-is.
    productCategory:
      pf.id === NIL_UUID || !hasStringValue(pf.productCategory) ? null : pf.productCategory,
    intervalLength: pf.intervalLength.toISO() ?? '',
    intervalStart: pf.intervalStart.toISO() ?? '',
  })),
  capacityForecasts: Forecast.fillForecasts({
    currentForecasts: plantData?.forecasts ?? [],
    kind: Enums.ForecastKind.Capacity,
    tenantUnits: getTenantUnits(),
    // Same rationale as budgetForecasts above.
  }).map((pf): PlantFormikType['capacityForecasts'][number] => ({
    id: pf.id,
    kind: pf.kind,
    value: pf.value,
    unit: pf.unit,
    // See budgetForecasts above.
    productCategory:
      pf.id === NIL_UUID || !hasStringValue(pf.productCategory) ? null : pf.productCategory,
    intervalLength: pf.intervalLength.toISO() ?? '',
    intervalStart: pf.intervalStart.toISO() ?? '',
  })),
  materialCostRule: FormikCostRule(plantData?.materialCostRule, plantData?.currencyCode),
  isArchived: plantData?.isArchived ?? false,
});

export const NewDeliveryCostOrNullFromFormik = (
  values: YupSchemaNullableDeliveryCostsType,
  dirtyEdits?: PartialDeep<YupSchemaNullableDeliveryCostsType>,
): DeliveryCost | null => {
  if (values === null && Object.keys(dirtyEdits ?? {}).length === 0) {
    return null;
  }

  const schema = YupDeliveryCosts('Delivery costs');
  const input: YupSchemaNullableDeliveryCostsType = mergeObjects({}, values, dirtyEdits);

  const drivingTimeAdjustmentPercentage = Number.isNaN(
    parseFloat(input?.drivingTimeAdjustmentPercentage ?? ''),
  )
    ? null
    : (parseFloat(input?.drivingTimeAdjustmentPercentage ?? '') / 100).toFixed(3);
  const unengagedTimeAdjustmentPercentage = Number.isNaN(
    parseFloat(input?.unengagedTimeAdjustmentPercentage ?? ''),
  )
    ? null
    : (parseFloat(input?.unengagedTimeAdjustmentPercentage ?? '') / 100).toFixed(3);

  const transformed = mergeObjects({}, input, {
    drivingTimeAdjustmentPercentage,
    unengagedTimeAdjustmentPercentage,
  });
  let castData: Parameters<typeof NewDeliveryCostFromWireFormat>[0];
  try {
    castData = RemoveNullProperties(schema.cast(transformed));
    // HACK: fill in default zero values for nullable sub-structs that are always non-null in practice.
    // This can't happen as part of the earlier merge because then nullable properties get their zero
    // defaults stripped by RemoveNullProperties.
    castData = mergeObjects(DeliveryCost.zero(), castData);
  } catch (e) {
    try {
      schema.validateSync(transformed, {
        strict: true,
        abortEarly: false,
      });
      logger.error(`NewDeliveryCostFromFormik parsing failed, but validation succeeded?! ${e}`);
    } catch (error) {
      const details =
        error instanceof Yup.ValidationError
          ? error.inner.map((inner) => `${inner.path}: ${inner.type}`).join('\n')
          : '';
      logger.error(`NewDeliveryCostFromFormik parsing failed. ${error}\n${details}`);
    }
    castData = {};
  }
  return NewDeliveryCostFromWireFormat(castData);
};

export const NewPlantFromFormik = (
  values: PlantFormikType,
  dirtyEdits?: PartialDeep<PlantFormikType>,
  marginConfiguration?: Enums.MarginConfiguration,
): Plant => {
  const input: PlantFormikType = mergeObjects({}, values, dirtyEdits);

  // Keep form validation/casting as-is (form only has two fields)
  let castData: Parameters<typeof NewPlantFromWireFormat>[0];
  try {
    const transformedForValidation = mergeObjects({}, input, {
      forecasts: [...input.capacityForecasts, ...input.budgetForecasts].map((f) => ({
        ...f,
        value: cleanedDecimal(f.value),
      })),
      materialCostRule: {
        markupRatio: (() => {
          const stripped = (input.materialCostRule?.markupRatio ?? '').replaceAll(',', '');
          return Number.isNaN(parseFloat(stripped))
            ? null
            : (parseFloat(stripped) / 100).toFixed(3);
        })(),
      },
      deliveryCosts: NewDeliveryCostOrNullFromFormik(input.deliveryCosts),
    });

    castData = RemoveNullProperties(PlantSchemaFormik.cast(transformedForValidation));

    // HACK: fill in default zero values for nullable sub-structs that are always non-null in practice.
    // This can't happen as part of the earlier merge because then nullable properties get their zero
    // defaults stripped by RemoveNullProperties.
    castData = mergeObjects(
      {
        deliveryCosts: DeliveryCost.zero(),
        readyMixConstants: ReadyMixConstants.zero(),
      },
      castData,
    );
  } catch {
    castData = {};
  }

  // Map the two form fields to four backend override fields, preserving untouched fields
  const rmc = input.readyMixConstants ?? ReadyMixConstants.zero();
  const {
    targetMargin,
    minimumMargin,
    marginsInheritedFrom,
    targetMarginPercentageOverride,
    minimumMarginPercentageOverride,
    targetMarginFlatAmountOverride,
    minimumMarginFlatAmountOverride,
  } = rmc as ReadyMixConstants & {
    targetMargin?: string;
    minimumMargin?: string;
    marginsInheritedFrom: string;
  };
  const cfg = marginConfiguration ?? Enums.MarginConfiguration.Percentage;

  const newOverrides = {
    minimumMarginPercentageOverride,
    targetMarginPercentageOverride,
    minimumMarginFlatAmountOverride,
    targetMarginFlatAmountOverride,
  };
  const overrides = marginsInheritedFrom === 'location';
  if (cfg === Enums.MarginConfiguration.Percentage) {
    newOverrides.targetMarginPercentageOverride = overrides
      ? percentToRatio(targetMargin?.trim())
      : null;
    newOverrides.minimumMarginPercentageOverride = overrides
      ? percentToRatio(minimumMargin?.trim())
      : null;
  } else if (cfg === Enums.MarginConfiguration.FlatDollar) {
    const currency = resolveCurrencyCode(input.currencyCode);
    const newTargetMargin = cleanedNullableDecimal(targetMargin);
    newOverrides.targetMarginFlatAmountOverride = overrides
      ? new Currency({ number: newTargetMargin, currency })
      : null;
    const newMinimumMargin = cleanedNullableDecimal(minimumMargin);
    newOverrides.minimumMarginFlatAmountOverride = overrides
      ? new Currency({ number: newMinimumMargin, currency })
      : null;
  }

  const plantForDomain = mergeObjects({}, castData, {
    readyMixConstants: {
      sgaCost: castData.readyMixConstants?.sgaCost ?? ReadyMixConstants.zero().sgaCost,
      operatingCost:
        castData.readyMixConstants?.operatingCost ?? ReadyMixConstants.zero().operatingCost,
      ...newOverrides,
    },
    forecasts: [...input.capacityForecasts, ...input.budgetForecasts].map((f) => ({
      ...f,
      value: cleanedDecimal(f.value),
    })),
  });

  return NewPlantFromWireFormat(plantForDomain);
};
